import { Injectable } from '@angular/core';
import { Issue, IssueSummary, Project } from 'src/app/shared/models';
import { AppHttpService } from 'src/app/shared/services';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { v1URL } from 'src/app/shared/constants/urls.constants';
import { IssueExportFormat, IssueMapMeta, IssueMapRow } from 'src/app/shared/helpers/issues-tab';
// import { Issue, IssueSummary } from '../models';

/** "<project> - Issues.<ext>" out of a Content-Disposition header, else the fallback. */
export function issueExportFileName(contentDisposition: string | null | undefined, fallback: string): string {
  const match = contentDisposition?.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  const name = match?.[1]?.replace(/['"]/g, '').trim();
  return name || fallback;
}

@Injectable({ providedIn: 'root' })
export class IssueService {

  fetchIssuesList: Subject<any> = new Subject<any>();

  constructor(
    private appHttpService: AppHttpService
  ) { }

  fetchRecords(project: Project, pagination: any = {}, qp: any = {}): Promise<any> {
    qp.projectId = qp.projectId ?? project.id;
    qp.start = pagination.pageIndex * pagination.pageSize;
    qp.total = pagination.pageSize;

    return this.appHttpService.getService(`/v1/issue`, qp).toPromise()
      .then(resp => {
        if (resp.data && Array.isArray(resp.data)) {
          const result = resp.data.map(r => new Issue(r));
          const meta = Object.assign(resp.meta || {}, { type: resp.type });
          return { result, meta };
        }
        throw new Error('Error parsing response');
      });
  }

  /** Fetches a single issue by id (used to deep-link an issue that isn't on the current page). */
  fetchOne(id: string): Promise<Issue> {
    return this.appHttpService.getService(`/v1/issue/${id}`).toPromise()
      .then(resp => new Issue(resp.data));
  }

  /**
   * Fetches entity's issue summary
   * @param entityType string
   * @param id string
   * @returns {Promise<IssueSummary>}
   */
  fetchSummary(entityType: string, id: string) {
    return this.appHttpService.getService(`/v1/issue/summary/${entityType}/${id}`).toPromise()
      .then(resp => new IssueSummary(resp.data));
  }

  /**
   * Downloads the project's issues as a PDF / CSV / KMZ file (GET
   * /v1/issue/export/:format) with the Issues tab's filters as the query.
   * The filename comes from Content-Disposition. Rejects with the server's
   * message (an error body arrives as a Blob) or a generic one.
   */
  exportIssues(format: IssueExportFormat, qp: Record<string, string>, fallbackName: string): Promise<void> {
    return this.appHttpService
      .getService(v1URL.issue.export.url({ format }), qp, { responseType: 'blob', observe: 'response' })
      .toPromise()
      .then((resp: HttpResponse<Blob> | null) => {
        if (!(resp?.body instanceof Blob)) {
          throw new Error('Export failed');
        }
        saveAs(resp.body, issueExportFileName(resp.headers?.get('Content-Disposition'), fallbackName));
      }, async (err: { error?: unknown }) => {
        let message = 'Export failed';
        try {
          type ErrorBody = { message?: string; error?: string | { message?: string } };
          const body: ErrorBody = err?.error instanceof Blob ? JSON.parse(await err.error.text()) : (err?.error as ErrorBody);
          const nested = typeof body?.error === 'string' ? body.error : body?.error?.message;
          message = nested || body?.message || message;
        } catch { /* keep default */ }
        throw new Error(message);
      });
  }

  /**
   * Every issue matching the Issues tab's filters that has coordinates
   * (GET /v1/issue/for-map), for the pins on the project map, with the
   * meta (how many matching issues have no location, and which).
   */
  fetchForMap(qp: Record<string, string>): Observable<{ rows: IssueMapRow[]; meta: IssueMapMeta }> {
    return this.appHttpService.getService(v1URL.issue.forMap.url(), qp).pipe(
      map((resp: { data?: IssueMapRow[]; meta?: IssueMapMeta } | IssueMapRow[]) => (Array.isArray(resp)
        ? { rows: resp, meta: {} }
        : { rows: resp?.data || [], meta: resp?.meta || {} })),
    );
  }
}
